<template>
    <vs-popup :active.sync="open" title="Upload patient files" @close="handleClose" button-close-hidden>
      <div class="p-6 relative">
        <button
          @click="handleClose"
          class="absolute top-4 right-0 mr-4 rounded-full bg-transparent border-transparent"
          aria-label="Close dialog"
        >
          <XIcon class="w-5 h-5 text-gray-600 hover:text-gray-800" stroke-width="3" />
        </button>
        
        <div class="text-lg font-semibold text-gray-800 mb-2">Upload patient files</div>
        <p class="text-sm text-gray-600 mb-4">
          Upload a photo or file to patient's profile
        </p>
        <FileUpload :files="uploadFiles" :isLoading="isUploading" @update:files="handleFileUpdate" @remove:files="handleFileRemove" />
        <div class="flex justify-end space-x-3 mt-6">
          <vs-button
            data-testid="cancel-upload"
            type="border"
            class="font-semibold text-primary border-gray-600 p-3"
            @click="handleClose"
          >
            Cancel
          </vs-button>
          <vs-button
            data-testid="add-to-gallery"
            @click="handleUploadFiles(uploadFiles)"
            :disabled="isUploading || uploadFiles.length === 0"
            class="font-semibold p-3"
          >
            <div class="flex items-center">
              <LoaderIcon v-if="isUploading" class="w-4 h-4 mr-2 animate-spin" />
              Add to Files
            </div>
          </vs-button>
        </div>
      </div>
    </vs-popup>
  </template>
  
<script>
import { XIcon, LoaderIcon } from 'vue-feather-icons';
import FileUpload from '@/views/components/file-upload/FileUpload.vue';

export default {
  name: 'PatientFileUploadDialog',
  props: {
    open: {
      type: Boolean,
      required: true,
    },
    files: {
      type: Array,
      default: () => [],
    },
  },
  components: {
    FileUpload,
    XIcon,
    LoaderIcon,
  },
  data() {
    return {
      uploadFiles: [],
      isUploading: false,
    };
  },
  methods: {
    setOpen(open)  {
      this.$emit('update:open', open);
    },
    async handleFileUpdate(files) {
      this.uploadFiles = files;
      if (files.length > 0) {
        this.isUploading = true;
        try {
          const patientId = this.$route.params.patientId;
          const presignedUrlPromises = files.map(file => {
            const dataFile = file.file ? file.file : file;
            return this.$store.dispatch('patient/fetchPresignedUrl', {
              filename: dataFile.name,
              patientId: patientId,
              contentType: dataFile.type,
            });
          });
          const presignedUrls = await Promise.all(presignedUrlPromises);

          this.uploadFiles = files.map((file, index) => {
            return {
              file: file,
              presignedUrl: presignedUrls[index].data,
            };
          });
        } catch (error) {
          this.$vs.notify({
            title: 'Error',
            text: 'Failed to prepare files for upload',
            color: 'danger',
          });
        } finally {
          this.isUploading = false;
        }
      }
    },
    
    handleFileRemove(files) {
      this.uploadFiles = files;
    },
      
    handleClose(){
      this.$emit('close');
      this.uploadFiles = [];
    },
    async handleUploadFiles(files) {
      this.isUploading = true;
      const patientId = this.$route.params.patientId;

      const uploadResults = [];
      const failedFiles = [];
      
      for (let index = 0; index < files.length; index++) {
        const file = files[index];
        
        try {
          const result = await this.$store.dispatch('patient/uploadFileToPresignedUrl', {
            presignedUrl: file.presignedUrl.data.uploadUrl,
            file: file.file,
          });

          const filesData = {
            name: file.file.name,
            url: (() => {
              try {
                const url = new URL(file.presignedUrl.data.uploadUrl);
                return url.origin + url.pathname;
              } catch (e) {
                return file.presignedUrl.data.uploadUrl.split('?')[0];
              }
            })(),
            type: file.file.name.split('.').pop() || file.file.type.split('/')[1] || file.file.type,
          };
          
          await this.$store.dispatch('patient/createPatientFile', {
            patientId: patientId,
            files: [filesData],
          });
          
          uploadResults.push(result);
        } catch (error) {
          failedFiles.push(file);
          console.error(`Failed to upload ${file.file.name}:`, error);
        }
      }
      
      if (failedFiles.length === 0) {
        this.$vs.notify({
          title: 'Success',
          text: 'All files uploaded successfully!',
          color: 'success',
        });
        
        this.setOpen(false);
        this.uploadFiles = [];
        
        this.$emit('refetchPatientFiles', patientId);
      } else if (uploadResults.length > 0) {
        this.$vs.notify({
          title: 'Partial Success',
          text: `${uploadResults.length} files uploaded successfully, ${failedFiles.length} files failed.`,
          color: 'warning',
        });
        
        this.uploadFiles = failedFiles;
        this.$emit('refetchPatientFiles', patientId);
      } else {
        this.$vs.notify({
          title: 'Upload Failed',
          text: 'All files failed to upload. Please try again.',
          color: 'danger',
        });
        
      }

      this.isUploading = false;
    },
  },
};
</script>